import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiService } from "../services/api";
import { queryKeys } from "../lib/queryKeys";
import { getApiErrorMessage } from "../lib/apiErrors";
import type { ParticipantResponse, EligibleParticipantResponse } from "../types/tickets";

/**
 * Hook to get participants for a ticket
 */
export function useTicketParticipants(ticketId: string | undefined, organizationId: string | undefined) {
  return useQuery({
    queryKey: [...queryKeys.tickets.participants(ticketId!), organizationId],
    queryFn: async () => {
      const response = await apiService.getTicketParticipants(ticketId!, organizationId);
      if (!response.success || !response.data) {
        throw new Error(getApiErrorMessage(response, 'Failed to fetch participants'));
      }
      return response.data as ParticipantResponse[];
    },
    enabled: !!ticketId && !!organizationId,
    staleTime: 30000,
  });
}

/**
 * Hook to search eligible participants for a ticket
 */
export function useEligibleParticipants(ticketId: string | undefined, organizationId: string | undefined, search?: string) {
  return useQuery({
    queryKey: [...queryKeys.tickets.participants(ticketId!), 'eligible', organizationId, search],
    queryFn: async () => {
      const response = await apiService.getEligibleParticipants(ticketId!, organizationId, search);
      if (!response.success || !response.data) {
        throw new Error(getApiErrorMessage(response, 'Failed to fetch eligible participants'));
      }
      return response.data as EligibleParticipantResponse[];
    },
    enabled: !!ticketId && !!organizationId && (search === undefined || search.length >= 0),
    staleTime: 10000,
  });
}

/**
 * Hook to add a participant to a ticket
 */
export function useAddParticipant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ ticketId, customerId, organizationId }: { ticketId: string; customerId: string; organizationId?: string }) => {
      const response = await apiService.addTicketParticipant(ticketId, customerId, organizationId);
      if (!response.success || !response.data) {
        throw new Error(getApiErrorMessage(response, 'Failed to add participant'));
      }
      return response.data;
    },
    onSuccess: (_, { ticketId }) => {
      // Invalidate participants query
      queryClient.invalidateQueries({
        queryKey: queryKeys.tickets.participants(ticketId),
      });
    },
  });
}

/**
 * Hook to add multiple participants to a ticket
 */
export function useAddParticipants() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ ticketId, customerIds, organizationId }: { ticketId: string; customerIds: string[]; organizationId?: string }) => {
      const response = await apiService.addTicketParticipants(ticketId, customerIds, organizationId);
      if (!response.success || !response.data) {
        throw new Error(getApiErrorMessage(response, 'Failed to add participants'));
      }
      return response.data;
    },
    onSuccess: (_, { ticketId }) => {
      // Invalidate participants query
      queryClient.invalidateQueries({
        queryKey: queryKeys.tickets.participants(ticketId),
      });
    },
  });
}

/**
 * Hook to remove a participant from a ticket
 */
export function useRemoveParticipant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ ticketId, customerId, organizationId }: { ticketId: string; customerId: string; organizationId?: string }) => {
      const response = await apiService.removeTicketParticipant(ticketId, customerId, organizationId);
      if (!response.success) {
        throw new Error(getApiErrorMessage(response, 'Failed to remove participant'));
      }
      return response.data;
    },
    onSuccess: (_, { ticketId }) => {
      // Invalidate participants query
      queryClient.invalidateQueries({
        queryKey: queryKeys.tickets.participants(ticketId),
      });
    },
  });
}
